/**
 * FoodPickerModal — modal para elegir alimentos del catálogo (Feature 4).
 *
 * Mismo patrón que ExercisePickerModal pero más liviano: el "detalle" de un
 * alimento son solo 4 macros, así que la lista los muestra inline y el click
 * agrega directo (sin sub-modal de detalle).
 *
 * Layout:
 *   ┌──────────┬───────────────────────────────┐
 *   │ Filtros  │  Búsqueda + lista de alimentos │
 *   │ (cat.)   │  (nombre + macros por 100 g)   │
 *   │ + Custom │                                │
 *   └──────────┴───────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { invalidateFoodCatalog, loadFoodCatalog, type Food } from "../../api/foods";
import { Icon } from "../../lib/Icon";
import { normalize } from "../../lib/normalizeText";

interface FoodPickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (food: Food) => void;
}

const ALL_CATEGORIES = "__all__";
const PAGE_SIZE = 60;

/** Etiqueta de la porción base de un alimento ("por 100 g", "por unidad"…). */
function basePortionLabel(f: Food): string {
  const u = f.base_unit || "g";
  if (u === "g" || u === "ml") return `por ${f.base_quantity} ${u}`;
  return f.base_quantity === 1 ? `por ${u}` : `por ${f.base_quantity} ${u}`;
}

export function FoodPickerModal({ open, onClose, onPick }: FoodPickerModalProps) {
  const [foods, setFoods] = useState<Food[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selectedCat, setSelectedCat] = useState<string>(ALL_CATEGORIES);
  const [limit, setLimit] = useState(PAGE_SIZE);
  /** Form "crear alimento personalizado" (null = cerrado). */
  const [customForm, setCustomForm] = useState<{
    name: string;
    qty: string;
    unit: string;
    kcal: string;
    protein: string;
    carbs: string;
    fat: string;
  } | null>(null);

  // Carga del catálogo al abrir
  useEffect(() => {
    if (!open || loaded || loading) return;
    setLoading(true);
    setLoadError(false);
    loadFoodCatalog()
      .then((list) => {
        setFoods(list);
        setLoaded(true);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [open, loaded, loading]);

  // Debounce de la búsqueda
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  // ESC cierra (primero el form custom si está abierto)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (customForm) setCustomForm(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, customForm]);

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setQ("");
      setDebouncedQ("");
      setSelectedCat(ALL_CATEGORIES);
      setLimit(PAGE_SIZE);
      setCustomForm(null);
    }
  }, [open]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [debouncedQ, selectedCat]);

  // Chips de categoría con conteo
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of foods) {
      const c = f.category || "Otros";
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
  }, [foods]);

  const list = useMemo<Food[]>(() => {
    const nq = normalize(debouncedQ);
    return foods.filter((f) => {
      if (selectedCat !== ALL_CATEGORIES && (f.category || "Otros") !== selectedCat) {
        return false;
      }
      if (nq && !normalize(f.name).includes(nq)) return false;
      return true;
    });
  }, [foods, selectedCat, debouncedQ]);

  const visible = list.slice(0, limit);

  const handleCustomSubmit = () => {
    if (!customForm || !customForm.name.trim()) return;
    const qty = Number(customForm.qty) || 100;
    // El alimento custom guarda sus macros como "por la cantidad ingresada".
    const custom: Food = {
      id: -Date.now(), // id negativo = custom (sin referencia a food_library)
      name: customForm.name.trim(),
      base_unit: customForm.unit.trim() || "g",
      base_quantity: qty,
      calories: Number(customForm.kcal) || 0,
      protein: Number(customForm.protein) || 0,
      carbs: Number(customForm.carbs) || 0,
      fat: Number(customForm.fat) || 0,
      category: "Personalizado",
      data_source: "custom",
    };
    onPick(custom);
    setCustomForm(null);
  };

  if (!open) return null;

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div className="al-modal al-food-picker" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Catálogo de alimentos
            </h3>
            <div className="tiny muted">
              {loadError
                ? "No pude cargar el catálogo"
                : !loaded && loading
                ? "Cargando catálogo…"
                : `${list.length} alimentos${
                    selectedCat !== ALL_CATEGORIES ? ` en ${selectedCat}` : ""
                  } · macros por porción base`}
              {loaded && (
                <>
                  {" · "}
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      invalidateFoodCatalog();
                      setLoaded(false);
                    }}
                    title="Volver a cargar el catálogo desde el servidor"
                  >
                    Recargar
                  </button>
                </>
              )}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-food-picker-body">
          {/* Sidebar: categorías + crear personalizado */}
          <aside className="al-food-picker-side">
            <div className="al-ex-picker-side-title">CATEGORÍA</div>
            <div className="al-ex-chips">
              <button
                type="button"
                className={"al-ex-chip" + (selectedCat === ALL_CATEGORIES ? " active" : "")}
                onClick={() => setSelectedCat(ALL_CATEGORIES)}
              >
                <span>Todas</span>
                <span style={{ opacity: 0.7, fontSize: 11 }}>{foods.length}</span>
              </button>
              {categories.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={"al-ex-chip" + (selectedCat === c.key ? " active" : "")}
                  onClick={() => setSelectedCat(c.key)}
                >
                  <span>{c.key}</span>
                  <span style={{ opacity: 0.7, fontSize: 11 }}>{c.count}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="al-ex-picker-side-action"
              onClick={() =>
                setCustomForm({
                  name: q,
                  qty: "100",
                  unit: "g",
                  kcal: "",
                  protein: "",
                  carbs: "",
                  fat: "",
                })
              }
              title="Crear un alimento que no está en el catálogo"
            >
              + Crear personalizado
            </button>
          </aside>

          {/* Main: búsqueda + lista */}
          <main className="al-food-picker-main">
            <div className="al-ex-search">
              <Icon name="search" size={16} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar pollo, arroz, avena…"
                autoFocus
              />
              {q && (
                <button className="link-btn" onClick={() => setQ("")} type="button">
                  limpiar
                </button>
              )}
            </div>

            {loadError ? (
              <div className="al-ex-empty">
                No pude cargar el catálogo de alimentos.
                <br />
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 12 }}
                  type="button"
                  onClick={() => {
                    setLoaded(false);
                    setLoadError(false);
                  }}
                >
                  Reintentar
                </button>
              </div>
            ) : !loaded && loading ? (
              <div className="al-ex-empty">Cargando catálogo de alimentos…</div>
            ) : list.length === 0 ? (
              <div className="al-ex-empty">
                Sin resultados{debouncedQ ? ` para "${debouncedQ}"` : ""}.
                <br />
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 12 }}
                  type="button"
                  onClick={() =>
                    setCustomForm({
                      name: debouncedQ,
                      qty: "100",
                      unit: "g",
                      kcal: "",
                      protein: "",
                      carbs: "",
                      fat: "",
                    })
                  }
                >
                  + Crear "{debouncedQ || "alimento personalizado"}"
                </button>
              </div>
            ) : (
              <div className="al-food-list">
                <div className="al-food-row al-food-row-head">
                  <span>Alimento</span>
                  <span>Kcal</span>
                  <span>P</span>
                  <span>C</span>
                  <span>G</span>
                </div>
                {visible.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="al-food-row"
                    onClick={() => onPick(f)}
                    title={`Agregar ${f.name}`}
                  >
                    <span className="al-food-row-name">
                      {f.name}
                      <span className="al-food-row-cat">
                        {f.category ? ` · ${f.category}` : ""} · {basePortionLabel(f)}
                      </span>
                    </span>
                    <span>{Math.round(f.calories)}</span>
                    <span>{f.protein}</span>
                    <span>{f.carbs}</span>
                    <span>{f.fat}</span>
                  </button>
                ))}
                {visible.length < list.length && (
                  <div className="al-ex-more">
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={() => setLimit((l) => l + PAGE_SIZE)}
                    >
                      Cargar más ({list.length - visible.length} restantes)
                    </button>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>

        {/* Form "crear alimento personalizado" */}
        {customForm && (
          <div className="al-ex-custom-overlay" onClick={() => setCustomForm(null)}>
            <div className="al-ex-custom-modal" onClick={(e) => e.stopPropagation()}>
              <div className="al-modal-hd">
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
                  Crear alimento personalizado
                </h3>
                <button
                  className="icon-btn"
                  onClick={() => setCustomForm(null)}
                  title="Cerrar"
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
              <div
                style={{
                  padding: "16px 20px 20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div className="tiny muted">
                  Ingresá los macros para la cantidad indicada. Después podés
                  cambiar la cantidad en la comida y se reescalan solos.
                </div>
                <label className="al-food-custom-field">
                  <span className="tiny" style={{ fontWeight: 600 }}>Nombre *</span>
                  <input
                    type="text"
                    className="al-input"
                    value={customForm.name}
                    onChange={(e) => setCustomForm({ ...customForm, name: e.target.value })}
                    placeholder="Ej. Batido casero"
                    autoFocus
                  />
                </label>
                <div className="al-food-custom-grid">
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Cantidad</span>
                    <input
                      type="number"
                      className="al-input"
                      value={customForm.qty}
                      onChange={(e) => setCustomForm({ ...customForm, qty: e.target.value })}
                      placeholder="100"
                    />
                  </label>
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Unidad</span>
                    <input
                      type="text"
                      className="al-input"
                      value={customForm.unit}
                      onChange={(e) => setCustomForm({ ...customForm, unit: e.target.value })}
                      placeholder="g"
                    />
                  </label>
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Kcal</span>
                    <input
                      type="number"
                      className="al-input"
                      value={customForm.kcal}
                      onChange={(e) => setCustomForm({ ...customForm, kcal: e.target.value })}
                      placeholder="0"
                    />
                  </label>
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Proteína</span>
                    <input
                      type="number"
                      className="al-input"
                      value={customForm.protein}
                      onChange={(e) => setCustomForm({ ...customForm, protein: e.target.value })}
                      placeholder="0"
                    />
                  </label>
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Carbos</span>
                    <input
                      type="number"
                      className="al-input"
                      value={customForm.carbs}
                      onChange={(e) => setCustomForm({ ...customForm, carbs: e.target.value })}
                      placeholder="0"
                    />
                  </label>
                  <label className="al-food-custom-field">
                    <span className="tiny" style={{ fontWeight: 600 }}>Grasa</span>
                    <input
                      type="number"
                      className="al-input"
                      value={customForm.fat}
                      onChange={(e) => setCustomForm({ ...customForm, fat: e.target.value })}
                      placeholder="0"
                    />
                  </label>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setCustomForm(null)}
                    type="button"
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCustomSubmit}
                    disabled={!customForm.name.trim()}
                    type="button"
                    style={{ flex: 1 }}
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
